import { memo, ReactNode } from "react";
import { IIssueRatingCategory } from "types";
import { CATEGORY_ACCENT_COLORS } from "const/common";
import { SearchIcon } from "@heroicons/react/outline";

interface CategoryPillBarProps {
  categories: IIssueRatingCategory[];
  selectedCategories: string[];
  onCategoryToggle: (categoryId: string) => void;
  onClearCategories?: () => void;
  allLabel?: string;
  // Action buttons integration
  showFiltersButton?: boolean;
  onFiltersClick?: () => void;
  actionButton?: ReactNode;
}

function CategoryPillBar({
  categories,
  selectedCategories,
  onCategoryToggle,
  onClearCategories,
  allLabel = "All",
  showFiltersButton = false,
  onFiltersClick,
  actionButton,
}: CategoryPillBarProps) {
  const hasSelectedCategories = selectedCategories.length > 0;

  return (
    <div className="w-full py-3">
      <div className="flex items-start justify-between gap-4">
        {/* Left side: Category pills - horizontally scrollable on mobile */}
        <div className="flex items-center gap-2 flex-1 overflow-x-auto pb-2 md:flex-wrap md:overflow-x-visible md:pb-0 scrollbar-hide">
          {/* "All" pill - shown when no categories selected or as clear option */}
          <button
            type="button"
            onClick={() => {
              if (hasSelectedCategories && onClearCategories) {
                onClearCategories();
              }
            }}
            className="shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap"
            style={{
              background: !hasSelectedCategories ? "#1C1917" : "transparent",
              color: !hasSelectedCategories ? "white" : "#44403C",
              border: !hasSelectedCategories ? "none" : "1px solid #D6D3D1",
              fontFamily: "'Source Sans 3', sans-serif",
            }}
            onMouseEnter={(e) => {
              if (hasSelectedCategories) {
                e.currentTarget.style.background = "rgba(28, 25, 23, 0.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (hasSelectedCategories) {
                e.currentTarget.style.background = "transparent";
              }
            }}
          >
            {allLabel}
          </button>

          {/* Category pills */}
          {categories.map((category) => {
            const categoryId = category.id as string;
            const isSelected = selectedCategories.includes(categoryId);
            const accentColor =
              CATEGORY_ACCENT_COLORS[category.category] || "#C2410C";

            return (
              <button
                key={category.id}
                type="button"
                onClick={() => onCategoryToggle(categoryId)}
                className="shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap"
                style={{
                  background: isSelected ? accentColor : "transparent",
                  color: isSelected ? "white" : "#44403C",
                  border: isSelected ? "none" : "1px solid #D6D3D1",
                  fontFamily: "'Source Sans 3', sans-serif",
                  boxShadow: isSelected
                    ? `0 2px 8px -2px ${accentColor}40`
                    : "none",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = `${accentColor}15`;
                    e.currentTarget.style.borderColor = accentColor;
                    e.currentTarget.style.color = accentColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "#D6D3D1";
                    e.currentTarget.style.color = "#44403C";
                  }
                }}
              >
                {category.title}
              </button>
            );
          })}
        </div>

        {/* Right side: Action buttons - hidden on mobile (already in header) */}
        {(showFiltersButton || actionButton) && (
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {showFiltersButton && onFiltersClick && (
              <button
                type="button"
                onClick={onFiltersClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: "rgba(194, 65, 12, 0.08)",
                  color: "#C2410C",
                  border: "1.5px solid #C2410C",
                  fontFamily: "'Source Sans 3', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#C2410C";
                  e.currentTarget.style.color = "#FFFEF9";
                  e.currentTarget.style.boxShadow = "0 4px 12px -2px rgba(194, 65, 12, 0.3)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(194, 65, 12, 0.08)";
                  e.currentTarget.style.color = "#C2410C";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <SearchIcon className="h-4 w-4" />
                Search
              </button>
            )}
            {actionButton}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(CategoryPillBar);
